import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { LinkTo } from '@ember/routing';
import { debounce, next, once } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import $ from 'jquery';

import type { Service } from 'webapp-types';
import { ActivityLogEvent } from 'webapp/enums/activity-log';
import { isAlive, MD_BREAKPOINT } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import willDestroy from 'webapp/modifiers/will-destroy';
import BarLoaderComponent from 'webapp/components/bar-loader';
import BoardMobileFullscreenComponent from 'webapp/components/board/mobile-fullscreen';
import BoardWallComponent from 'webapp/components/board/wall';
import BoardsTabsNavComponent from 'webapp/components/boards/tabs-nav';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FilterButtonComponent from 'webapp/components/filter/button';
import ToolTipComponent from 'webapp/components/tool-tip';
import TopbarLogoImageComponent from 'webapp/components/topbar-logo-image';

import type Board from 'webapp/models-custom/board';
import BoardState from 'webapp/models-custom/board-state';
import FilterSortBoard from 'webapp/models-custom/filter-sort/board';

interface Args {
	model: Board[];
}

export default class BoardsTabsComponent extends Component<Args> {
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/activity-logs')
	declare mmActivityLogsService: Service.ModelManager.ActivityLogs;

	@tracked selectedBoard: Board | null = null;
	@tracked boardState: BoardState | null = null;
	@tracked isWallView = true;

	filterSort: FilterSortBoard;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const filterSort = new FilterSortBoard({
			getAllItems: () => {
				return this.args.model;
			},
			afterFilter: () => {
				this.selectFirstAvailable();
			},
		});
		this.filterSort = filterSort;

		next(() => {
			this.checkToRequest();
			this.setWallView();
		});
	}

	get selectedId() {
		return this.selectedBoard?.id;
	}

	get skipActions() {
		return !this.selectedBoard?.canCurrentUserEdit;
	}

	private initializeSelectedBoard() {
		const savedBoardId = this.mmBoardsService.getFlowViewSelectedBoardId();
		if (savedBoardId && this.args.model.find((b) => b.id === savedBoardId)) {
			this.selectBoard(savedBoardId);
		} else {
			this.selectFirstAvailable();
		}
	}

	selectFirstAvailable() {
		const results = this.filterSort.results;
		if (!this.selectedBoard || !results.includes(this.selectedBoard)) {
			this.reallySelectBoard(results[0] || null);
			this.initBoardState();
		}
	}

	checkToRequest() {
		const boardId = this.selectedId;
		if (boardId) {
			void this.mmBoardsService.requestBoardData(boardId);
		}
	}

	initBoardState() {
		const board = this.selectedBoard;
		if (board) {
			if (this.boardState) {
				this.boardState.destroy(true); // also DELETE the preview
			}
			const boardState = BoardState.createFromBoard(board);
			this.boardState = boardState;
		}
	}

	@action
	setWallView() {
		if (isAlive(this)) {
			this.isWallView = $(window).width()! >= MD_BREAKPOINT;
		}
	}

	@action
	selectBoard(boardId: string) {
		this.mmBoardsService.setFlowViewSelectedBoardId(boardId);
		const board = this.args.model.find((item) => item.id === boardId);
		if (board) {
			this.reallySelectBoard(board);
		}
	}

	@action
	resetFilters() {
		this.filterSort.resetFilters();
	}

	@action
	setupView() {
		this.initializeSelectedBoard();
		$(window).on('resize.boardsTabs', () => {
			debounce(this, this.setWallView, 200);
		});
	}

	@action
	teardownView() {
		$(window).off('resize.boardsTabs');

		if (!this.boardState) {
			return;
		}

		this.boardState.destroy(true); // also DELETE the preview
	}

	private reallySelectBoard(board: Board | null) {
		this.selectedBoard = board;

		once(this, this.checkToRequest);
		once(this, this.initBoardState);

		if (board) {
			this.mmActivityLogsService.createAndSaveActivityLogItem(
				ActivityLogEvent.BOARD_VIEW,
				board.id!,
			);
		}
	}
	<template>
		<div id="boardsTabs" {{didInsert this.setupView}} {{willDestroy this.teardownView}}>
			<div id="topbar">

				<LinkTo @route="boards.index" class="logo">
					<TopbarLogoImageComponent @showBigLogo={{false}} />
				</LinkTo>

				{{#if this.selectedId}}
					<BoardsTabsNavComponent
						@model={{this.filterSort.results}}
						@selectedId={{this.selectedId}}
						@onOpen={{this.selectBoard}}
					/>
				{{/if}}

				<FilterButtonComponent @model={{this.filterSort}} />

				<div class="closeDesigner">
					<LinkTo @route="boards.index">
						<CommonUiIconComponent @name="Close" @size="lg" />
						<ToolTipComponent @text="Exit" />
					</LinkTo>
				</div>

			</div>

			{{#if this.filterSort.noResultsText}}
				<div class="noResults">
					<CommonUiIconComponent @name="Search" @size="2xl" @class="noResults-icon" />
					{{{this.filterSort.safeNoResultsText}}}
					<a role="button" {{on "click" this.resetFilters}}>Reset filter</a>
				</div>
			{{else}}

				{{#if this.selectedBoard}}
					<div id="datawall">
						{{#if this.boardState}}
							{{#if this.isWallView}}
								<BoardWallComponent
									@boardState={{this.boardState}}
									@skipActions={{this.skipActions}}
								/>
							{{else}}
								<BoardMobileFullscreenComponent
									@boardState={{this.boardState}}
									@currentZoom={{100}}
								/>
							{{/if}}
						{{/if}}
					</div>
				{{else}}
					<div id="loading">
						<BarLoaderComponent @size="big" />
					</div>
				{{/if}}

			{{/if}}
		</div>
	</template>
}
